import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { StudyInput } from "./StudyInput";
import { ContentUploadDialog } from "@/components/content/ContentUploadDialog";
import { PasteContentDialog } from "./PasteContentDialog";
import { Loader2 } from "lucide-react";
import { createChatFromStudyInput } from "@/lib/chat/createChatFromStudyInput";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCreateRecording } from "@/hooks/useCreateRecording";
import { toast } from "sonner";
import { checkUsageLimit, UsageLimitExceededError } from "@/hooks/useUsageLimits";
import { useUsageLimitContext } from "@/context/UsageLimitContext";
import { useTranslation } from "@/context/LanguageContext";
import type { Plan } from "@/lib/ai/models";

export const DashboardHeroStudy: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { createRecording, isCreating: isCreatingRecording } = useCreateRecording();
  const { showLimitError, setShowPricingDialog } = useUsageLimitContext();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [contentReferenceIds, setContentReferenceIds] = useState<string[]>([]);
  const [userPlan, setUserPlan] = useState<Plan>("free");

  // Fetch subscription status
  useEffect(() => {
    if (!user) return;
    const fetchPlan = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status")
        .eq("id", user.id)
        .single();
      if (data?.subscription_status === "active") {
        setUserPlan("pro");
      }
    };
    fetchPlan();
  }, [user]);
  
  const handleUploadClick = async () => {
    setIsCheckingLimit(true);
    try {
      const check = await checkUsageLimit("content_uploads");
      if (!check.canProceed) {
        showLimitError(new UsageLimitExceededError("content_uploads", check.used, check.limit));
        return;
      }
      setUploadDialogOpen(true);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  const handlePasteClick = async () => {
    setIsCheckingLimit(true);
    try {
      const check = await checkUsageLimit("content_uploads");
      if (!check.canProceed) {
        showLimitError(new UsageLimitExceededError("content_uploads", check.used, check.limit));
        return;
      }
      setPasteDialogOpen(true);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  const handleRecordClick = async () => {
    setIsCheckingLimit(true);
    try {
      const check = await checkUsageLimit("content_uploads");
      if (!check.canProceed) {
        showLimitError(new UsageLimitExceededError("content_uploads", check.used, check.limit));
        return;
      }
      createRecording(null);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  const handleSubmit = async (text: string) => {
    if (!user) {
      toast.error("Please sign in to start a chat");
      return;
    }

    if (!text.trim()) return;

    try {
      const chat = await createChatFromStudyInput({
        userId: user.id,
        firstMessage: text,
        roomId: null,
        searchEnabled: isSearchEnabled,
        contentReferenceIds: contentReferenceIds.length > 0 ? contentReferenceIds : undefined,
      });

      navigate(`/chat/${chat.id}`);
    } catch (error) {
      console.error("Error creating chat:", error);
      toast.error("Failed to start chat");
    }
  };

  const handleContentCreated = (contentId: string, contentType?: string) => {
    window.dispatchEvent(new CustomEvent("content-uploaded"));
    
    const mediaTypes = ["audio", "video", "recording", "youtube"];
    const docTypes = ["pdf", "doc", "web", "text"];
    const isMedia = mediaTypes.includes(contentType || "");
    const isDoc = docTypes.includes(contentType || "");

    // For PDFs, wait for chapters to be ready (no conversion needed)
    if (contentType === "pdf") {
      toast("Content added! Preparing your study materials...");

      const pdfChannel = supabase
        .channel(`content-ready-${contentId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "content",
            filter: `id=eq.${contentId}`,
          },
          (payload) => {
            const updated = payload.new as any;
            if (updated.chapters_status === "ready") {
              clearTimeout(pdfFallback);
              pdfChannel.unsubscribe();
              navigate(`/content/${contentId}`);
            }
          }
        )
        .subscribe();

      const pdfFallback = setTimeout(() => {
        pdfChannel.unsubscribe();
        navigate(`/content/${contentId}`);
      }, 15000);
      return;
    }

    toast("Content added! Preparing your study materials...");

    // Subscribe to realtime updates on the content row
    const channel = supabase
      .channel(`content-ready-${contentId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "content",
          filter: `id=eq.${contentId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          let ready = false;

          if (isMedia && updated.transcript_status === "ready") {
            ready = true;
          } else if (isDoc && (updated.chapters_status === "ready" || updated.text_extraction_status === "ready")) {
            ready = true;
          }

          if (ready) {
            clearTimeout(fallbackTimer);
            channel.unsubscribe();
            navigate(`/content/${contentId}`);
          }
        }
      )
      .subscribe();

    // Fallback: navigate after 15s regardless
    const fallbackTimer = setTimeout(() => {
      channel.unsubscribe();
      navigate(`/content/${contentId}`);
    }, 15000);
  };

  return (
    <div className="flex w-full flex-col items-center sm:min-h-[280px] min-h-[350px]">
      <div className="mb-5 flex flex-col items-center gap-2">
        <h2 className="text-xl text-center sm:text-3xl font-medium xl:text-4xl">
          {t("dashboard.whatToStudy")}
        </h2>
        <p className="text-base text-muted-foreground text-center">
          {t("dashboard.description")}
        </p>
      </div>

      <div className="z-10 w-full text-center md:max-w-lg xl:max-w-2xl flex flex-col">
        <div className="grid w-full grid-cols-1 gap-3 sm:flex sm:items-center sm:justify-center">
          <div className="w-full flex-1 sm:w-1/3">
            <div 
              onClick={handleUploadClick}
              className="group relative cursor-pointer rounded-3xl border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <div className="flex h-[112px] flex-col items-start justify-center gap-y-1 px-5 py-4 sm:flex-col">
                {isCheckingLimit && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-3xl">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                )}
                <div className="flex items-center gap-x-3 space-y-2 sm:block">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-upload mb-2 h-6 w-6 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                    aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" x2="12" y1="3" y2="15" />
                  </svg>
                  <div className="flex flex-col justify-center text-left">
                    <div className="flex items-center gap-x-1">
                      <h3 className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground sm:text-base">
                        {t("dashboard.upload")}
                      </h3>
                    </div>
                    <p className="text-xs text-left text-muted-foreground/70 transition-colors group-hover:text-muted-foreground sm:text-sm">
                      {t("dashboard.uploadDesc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex-1 sm:w-1/3">
            <div 
              onClick={handlePasteClick}
              className="group relative cursor-pointer rounded-3xl border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <div className="flex h-[112px] flex-col items-start justify-center gap-y-1 px-5 py-4 sm:flex-col">
                {isCheckingLimit && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-3xl">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                )}
                <div className="flex items-center gap-x-3 space-y-2 sm:block">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-link2 mb-2 h-6 w-6 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                    aria-hidden="true"
                  >
                    <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                    <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
                    <line x1="8" x2="16" y1="12" y2="12" />
                  </svg>
                  <div className="flex flex-col justify-center text-left">
                    <div className="flex items-center gap-x-1">
                      <h3 className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground sm:text-base">
                        {t("dashboard.paste")}
                      </h3>
                    </div>
                    <p className="text-xs text-left text-muted-foreground/70 transition-colors group-hover:text-muted-foreground sm:text-sm">
                      {t("dashboard.pasteDesc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex-1 sm:w-1/3">
            <div 
              onClick={handleRecordClick}
              className="group relative cursor-pointer rounded-3xl border border-border bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-md"
            >
              <div className="flex h-[112px] flex-col items-start justify-center gap-y-1 px-5 py-4 sm:flex-col">
                {(isCreatingRecording || isCheckingLimit) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-3xl">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                )}
                <div className="flex items-center gap-x-3 space-y-2 sm:block">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-mic mb-2 h-6 w-6 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                    aria-hidden="true"
                  >
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" x2="12" y1="19" y2="22" />
                  </svg>
                  <div className="flex flex-col justify-center text-left">
                    <div className="flex items-center gap-x-1">
                      <h3 className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground sm:text-base">
                        {t("dashboard.record")}
                      </h3>
                    </div>
                    <p className="text-xs text-left text-muted-foreground/70 transition-colors group-hover:text-muted-foreground sm:text-sm">
                      {t("dashboard.recordDesc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 w-full md:max-w-lg xl:max-w-2xl">
        <StudyInput
          onSendMessage={handleSubmit}
          onSearchStateChange={setIsSearchEnabled}
          onContentReferencesChange={setContentReferenceIds}
          placeholder={t("dashboard.learnAnything")}
          maxWidth="2xl"
          userPlan={userPlan}
          onUpgradeRequest={() => setShowPricingDialog(true)}
        />
      </div>

      <ContentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onContentCreated={handleContentCreated}
      />
      
      <PasteContentDialog
        open={pasteDialogOpen}
        onOpenChange={setPasteDialogOpen}
        onContentCreated={handleContentCreated}
      />
    </div>
  );
};
